import {b2c} from 'top20common/distribution/constants/scopes';
import auth from 'top20common/distribution/services/auth';
import languageService from 'top20common/distribution/services/languages';
import * as contextUtils from 'top20common/distribution/themes/utils';
import utils from 'top20common/distribution/utils';
import Vue from 'vue';
import GetTextPlugin from 'vue-gettext';
import Router from 'vue-router';

import store from '@/store';

import translations from '../../translations.json';

const DashboardPage = () => import('./pages/dashboard/DashboardPage');
const QuestionnairePage = () => import('./pages/questionnaire/Questionnaire');
const JobsPage = () => import('./pages/jobs/JobsPage');
const JobPage = () => import('./pages/job/JobPage');
const ReferralPage = () => import('./pages/referral/ReferralPage');

const EditProfilePage = () => import('./pages/profile/EditProfilePage');
const EditBasicProfileData = () => import('./pages/profile/edit-profile-parts/EditBasicProfileData');
const EditWorkExperience = () => import('./pages/profile/edit-profile-parts/experience/EditWorkExperience');
const EditEducation = () => import('./pages/profile/edit-profile-parts/education/EditEducation');
const EditProjects = () => import('./pages/profile/edit-profile-parts/projects/EditProjects');
const EditLanguages = () => import('./pages/profile/edit-profile-parts/EditLanguages');
const EditMotivators = () => import('./pages/profile/edit-profile-parts/EditMotivators');
const EditSkills = () => import('./pages/profile/edit-profile-parts/EditSkills');
const EditTrainings = () => import('./pages/profile/edit-profile-parts/trainings/EditTrainings');

const vue = new Vue();

Vue.use(GetTextPlugin, {
  availableLanguages: {
    en_US: 'English',
    zh_Hans: 'Chinese'
  },
  defaultLanguage: languageService.getCurrent(),
  translations: translations,
  silent: true
});

const title = contextUtils.getCurrentTitle();
const dashboard = vue.$gettext('Dashboard');
const questionnaire = vue.$gettext('Questionnaire');
const jobs = vue.$gettext('Jobs');
const job = vue.$gettext('Job');
const referral = vue.$gettext('Referral Detail');
const editProfile = vue.$gettext('Edit Profile');

const routes = [
  {
    path: '/:lang?/my/dashboard',
    name: 'dashboard',
    component: DashboardPage,
    meta: {title: `${dashboard} - ${title}`, scopes: [b2c], disableAutoScroll: true}
  },
  {
    path: '/:lang?/my/questionnaire',
    name: 'questionnaire',
    component: QuestionnairePage,
    meta: {title: `${questionnaire} - ${title}`, scopes: [b2c]}
  },
  {
    path: '/:lang?/jobs/:code/:title?',
    name: 'job',
    component: JobPage,
    meta: {
      title: `${job} - ${title}`,
      isOpen: true,
      analytics: to => {
        const code = to.params.code.toUpperCase();
        return to.params.lang ? `/${to.params.lang}/jobs/${code}` : `/jobs/${code}`;
      }
    }
  },
  {
    path: '/:lang?/jobs',
    name: 'jobs',
    component: JobsPage,
    meta: {title: `${jobs} - ${title}`, isOpen: true, analytics: to => to.fullPath}
  },
  {
    path: '/:lang?/my/referrals/:slug/:name?',
    name: 'referral',
    component: ReferralPage,
    meta: {
      title: `${referral} - ${title}`,
      scopes: [b2c],
      analytics: to => {
        return to.params.lang ? `/${to.params.lang}/my/referrals` : `/my/referrals`;
      }
    }
  },
  {
    path: '/:lang?/edit-profile',
    name: 'edit-profile-email',
    redirect: to => {
      const query = to && to.query ? to.query : {};
      return {path: '/:lang?/my/profile/edit/basic', query};
    },
    meta: {title: `${editProfile} - ${title}`, scopes: [b2c]}
  },
  {
    path: '/:lang?/my/profile/edit',
    name: 'edit-profile',
    component: EditProfilePage,
    redirect: {name: 'edit-basic-profile-data'},
    meta: {title: `${editProfile} - ${title}`, scopes: [b2c]},
    children: [
      {
        path: 'basic',
        name: 'edit-basic-profile-data',
        component: EditBasicProfileData,
        meta: {title: `${editProfile} - ${title}`, scopes: [b2c]}
      },
      {
        path: 'work-experience',
        name: 'edit-work-experience',
        component: EditWorkExperience,
        meta: {title: `${editProfile} - ${title}`, scopes: [b2c]}
      },
      {
        path: 'education',
        name: 'edit-education',
        component: EditEducation,
        meta: {title: `${editProfile} - ${title}`, scopes: [b2c]}
      },
      {
        path: 'projects',
        name: 'edit-projects',
        component: EditProjects,
        meta: {title: `${editProfile} - ${title}`, scopes: [b2c]}
      },
      {
        path: 'languages',
        name: 'edit-languages',
        component: EditLanguages,
        meta: {title: `${editProfile} - ${title}`, scopes: [b2c]}
      },
      {
        path: 'motivators',
        name: 'edit-motivators',
        component: EditMotivators,
        meta: {title: `${editProfile} - ${title}`, scopes: [b2c]}
      },
      {
        path: 'skills',
        name: 'edit-skills',
        component: EditSkills,
        meta: {title: `${editProfile} - ${title}`, scopes: [b2c]}
      },
      {
        path: 'training-certificates',
        name: 'edit-trainings',
        component: EditTrainings,
        meta: {title: `${editProfile} - ${title}`, scopes: [b2c]}
      }
    ]
  },
  {
    path: '/:lang?/refer/:referral_code/:job_code',
    name: 'refer-job',
    redirect: to => {
      return {name: 'job', params: {code: to.params.job_code}, query: {referral_code: to.params.referral_code}};
    },
    meta: {isOpen: true}
  }
];

const router = new Router({
  mode: 'history',
  base: process.env.VUE_APP_PREFIX || '/',
  routes,
  linkActiveClass: 'is-active',
  scrollBehavior(to, from, savedPosition) {
    return {x: 0, y: 0};
  }
});

router.beforeEach((to, from, next) => {
  auth.beforeEachRouteAuth(router, store, to, from, next);
});

router.afterEach((to, from) => {
  if (!to.meta.disableAutoScroll) {
    utils.afterEachRouteScroll(to, from, router, store);
  }
  document.title = to.meta.title || title;
  router.app.$mam.trackPageView(to);
});

export default router;
